import { FC, useCallback, useMemo } from 'react';
import { UseControllerProps } from 'react-hook-form';

import { gettext } from 'medialoopster/Internationalization';
import { Select, SelectOption } from 'medialoopster/components';

import useWidgetContext from '../hooks/useWidgetContext';

type Props = {
  readonly name: string;
  readonly label: string;
  readonly choices: ReadonlyArray<SelectOption>;
  readonly defaultValue: string | number | null;
  readonly allowEmpty: boolean;
  readonly emptyValue?: string | number | null;
  readonly disabled?: boolean;
  readonly requiredChoiceInfo?: string;
  readonly onChange?: (val: string | number | null) => void;
  readonly rules?: UseControllerProps['rules'];
};

const ChoiceWidget: FC<Props> = ({
  label,
  name,
  choices,
  defaultValue,
  allowEmpty,
  emptyValue = null,
  disabled = false,
  requiredChoiceInfo = gettext('Please select a value.'),
  rules = {},
  onChange,
}: Props) => {
  const {
    onChange: onWidgetChange,
    onBlur,
    value,
    error,
  } = useWidgetContext({
    name,
    defaultValue,
    rules: {
      validate: (v) => (v === emptyValue && !allowEmpty ? requiredChoiceInfo : true),
      ...rules,
    },
    disabled,
  });
  const selectChoices = useMemo(
    () => (allowEmpty ? [{ value: '', display: '---------' }, ...choices] : choices),
    [allowEmpty, choices],
  );
  const handleChange = useCallback(
    (newValue: string | number) => {
      const nextVal = newValue === '' ? emptyValue : newValue;
      onWidgetChange(nextVal);
      onChange?.(nextVal);
    },
    [onWidgetChange, onChange, emptyValue],
  );
  return (
    <Select
      id={name}
      label={label}
      choices={selectChoices}
      value={value?.toString() || ''}
      errorMsg={error}
      showError={!!error}
      onChange={handleChange}
      onBlur={onBlur}
      readOnly={disabled}
    />
  );
};

export default ChoiceWidget;
